import { ReactNode, useEffect, useRef } from 'react'
import homepageBg from '../../../assets/images/homepage/homepagebg.webp'

interface CommonHeroSectionProps {
  children: ReactNode
  wrapperClassName?: string
  contentClassName?: string
  backgroundImage?: string
  backgroundAlt?: string
}

const CommonHeroSection = ({
  children,
  wrapperClassName = 'relative overflow-hidden',
  contentClassName = 'relative z-20 w-full',
  backgroundImage = homepageBg,
  backgroundAlt = 'bg'
}: CommonHeroSectionProps) => {

  const glow1Ref = useRef<HTMLDivElement>(null)
  const glow2Ref = useRef<HTMLDivElement>(null)
  const sectionRef = useRef<HTMLElement>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    const section = sectionRef.current
    if (!section) return

    const glow1Size = 440
    const glow2Size = 500

    const BLEED = 0.35
    const MIN_DIST = 320

    type SineLayer = { freq: number; amp: number; phase: number }

    const g1 = {
      x: [
        { freq: 0.00017, amp: 0.38, phase: 0.00 },
        { freq: 0.00041, amp: 0.22, phase: 1.57 },
        { freq: 0.00071, amp: 0.12, phase: 3.10 },
        { freq: 0.00113, amp: 0.06, phase: 0.88 },
      ],
      y: [
        { freq: 0.00023, amp: 0.38, phase: 2.30 },
        { freq: 0.00059, amp: 0.22, phase: 0.44 },
        { freq: 0.00083, amp: 0.12, phase: 1.20 },
        { freq: 0.00137, amp: 0.06, phase: 4.10 },
      ],
    }

    const g2 = {
      x: [
        { freq: 0.00031, amp: 0.38, phase: 4.71 },
        { freq: 0.00053, amp: 0.22, phase: 2.09 },
        { freq: 0.00097, amp: 0.12, phase: 5.50 },
        { freq: 0.00061, amp: 0.06, phase: 1.30 },
      ],
      y: [
        { freq: 0.00019, amp: 0.38, phase: 1.05 },
        { freq: 0.00067, amp: 0.22, phase: 3.66 },
        { freq: 0.00089, amp: 0.12, phase: 0.25 },
        { freq: 0.00107, amp: 0.06, phase: 2.80 },
      ],
    }

    const noise = (layers: SineLayer[], t: number): number => {
      const totalAmp = layers.reduce((s, l) => s + l.amp, 0)
      const raw = layers.reduce((s, l) => s + Math.sin(t * l.freq + l.phase) * l.amp, 0)
      return raw / totalAmp // -1 .. 1
    }

    let t = 0

    const animate = () => {
      t += 16

      const rect = section.getBoundingClientRect()
      const W = rect.width
      const H = rect.height
      const bleed1X = glow1Size * BLEED
      const bleed1Y = glow1Size * BLEED
      const bleed2X = glow2Size * BLEED
      const bleed2Y = glow2Size * BLEED
      const rangeX1 = W - glow1Size + bleed1X * 2  // total travel range
      const rangeY1 = H - glow1Size + bleed1Y * 2
      const rangeX2 = W - glow2Size + bleed2X * 2
      const rangeY2 = H - glow2Size + bleed2Y * 2

      let x1 = (noise(g1.x, t) * 0.5 + 0.5) * rangeX1 - bleed1X
      let y1 = (noise(g1.y, t) * 0.5 + 0.5) * rangeY1 - bleed1Y
      let x2 = (noise(g2.x, t) * 0.5 + 0.5) * rangeX2 - bleed2X
      let y2 = (noise(g2.y, t) * 0.5 + 0.5) * rangeY2 - bleed2Y

      const cx1 = x1 + glow1Size / 2
      const cy1 = y1 + glow1Size / 2
      const cx2 = x2 + glow2Size / 2
      const cy2 = y2 + glow2Size / 2

      const dx = cx1 - cx2
      const dy = cy1 - cy2
      const dist = Math.sqrt(dx * dx + dy * dy) || 1

      if (dist < MIN_DIST) {
        const overlap = (MIN_DIST - dist) / MIN_DIST
        const push = overlap * 80
        const nx = dx / dist
        const ny = dy / dist

        x1 += nx * push
        y1 += ny * push
        x2 -= nx * push
        y2 -= ny * push
      }

      if (glow1Ref.current) {
        glow1Ref.current.style.transform = `translate(${x1}px, ${y1}px)`
      }
      if (glow2Ref.current) {
        glow2Ref.current.style.transform = `translate(${x2}px, ${y2}px)`
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  return (
    <section ref={sectionRef} className={wrapperClassName}>
      <img
        src={backgroundImage}
        alt={backgroundAlt}
        className="absolute inset-0 h-full w-full object-cover z-0"
        
      />

      {/* Glow 1 */}
      <div
        ref={glow1Ref}
        className="pointer-events-none absolute w-[450px] md:w-[440px] h-[450px] md:h-[440px] blur-[120px] opacity-60 z-10"
        style={{ background: 'radial-gradient(circle, #EA892A 0%, transparent 80%)' }}
      />

      {/* Glow 2 */}
      <div
        ref={glow2Ref}
        className="pointer-events-none absolute w-[450px] md:w-[650px] h-[450px]  md:h-[600px] blur-[120px] opacity-60 z-10"
        style={{ background: 'radial-gradient(circle, #E35384 0%, transparent 80%)' }}
      />

      <div className={contentClassName}>
        {children}
      </div>

      <div className="absolute bottom-0 left-0 z-30 h-[4px] w-full bg-purple" />
    </section>
  )
}

export default CommonHeroSection